import React, { useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCwIcon } from 'lucide-react';
import { ConstraintProfile, ConstraintDocument } from 'types/ConstraintProfileTypes';
import ConstraintsConfig from 'config/constraints-config.json';
import { FinecoButton } from 'pages/assistant/components/FinecoButton';
import { StarIcon } from './StarIcon';
import { computeDeltaCounts } from './profileCardUtils';

import styles from './savedProfiles.module.scss';

// Derive the section list straight from constraints-config.json. The ORDER of
// rows on a profile card and their LABELS must match the Vincoli panel
// accordions exactly — single source of truth, no parallel map to drift.
const SECTION_LIST = ConstraintsConfig.constraints as Array<{
  type: string;
  label: string;
}>;

type ProfileCardProps = {
  profile: ConstraintProfile;
  schemaDefaults: ConstraintDocument;
  // The synthetic "Predefiniti" placeholder card. When true the card drops the
  // rename and delete affordances (the built-in default can't be renamed or
  // removed) and its star locks once it is the active default — it is the
  // fallback configuration, so it can be selected but never deselected.
  isPredefinita?: boolean;
  onApply: (profile: ConstraintProfile) => void;
  onDelete: (profileId: string) => void;
  // Return a promise so the card can show a spinner on the star button while
  // the server round-trip (PATCH .../profiles/:id) completes. void/sync
  // returns are still accepted for backward compatibility.
  onToggleDefault: (profile: ConstraintProfile) => void | Promise<unknown>;
  // Returns a promise so the card can show the user's typed name with a
  // saving indicator while the server round-trip is in flight, then settle
  // on the catalog-refreshed value. void/sync returns are still accepted.
  onRename: (profile: ConstraintProfile, newName: string) => void | Promise<unknown>;
};

export const ProfileCard: React.FC<ProfileCardProps> = ({
  profile,
  schemaDefaults,
  isPredefinita = false,
  onApply,
  onDelete,
  onToggleDefault,
  onRename,
}) => {
  const { t } = useTranslation();
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(profile.name);
  // The star toggle round-trips to the server (updateProfile mutation). Without
  // a pending flag the click looks unresponsive — the user has no feedback
  // until the catalog refreshes a few hundred ms later. While pending, we
  // swap the star for a small spinner and disable the button so the user
  // can't queue a second toggle on top of the first.
  const [isTogglingDefault, setIsTogglingDefault] = useState(false);
  // Rename also round-trips to the server. While pending, render the user's
  // typed name (with a subtle saving indicator) instead of the still-stale
  // `profile.name` from the catalog — otherwise the user briefly sees the
  // old name, then a sudden swap once updateProfile returns.
  const [pendingRenameValue, setPendingRenameValue] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleToggleDefault = async () => {
    if (isTogglingDefault) return;
    setIsTogglingDefault(true);
    try {
      await onToggleDefault(profile);
    } finally {
      setIsTogglingDefault(false);
    }
  };

  const deltaCounts = useMemo(
    () => computeDeltaCounts(profile.constraints, schemaDefaults, profile.mode),
    [profile.constraints, schemaDefaults, profile.mode],
  );

  const isRebalancingProfile = profile.mode === 'rebalancing';

  const startRename = () => {
    setRenameValue(profile.name);
    setIsRenaming(true);
    // Focus the input after it mounts
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const confirmRename = async () => {
    const trimmed = renameValue.trim();
    setIsRenaming(false);
    if (!trimmed || trimmed === profile.name) return;
    setPendingRenameValue(trimmed);
    try {
      await onRename(profile, trimmed);
    } finally {
      // Catalog will have re-rendered ProfileCard with the new profile.name
      // by now; clear the pending overlay either way (success: identical to
      // catalog; failure: revert to the prior name so the UI doesn't lie).
      setPendingRenameValue(null);
    }
  };

  const cancelRename = () => {
    setRenameValue(profile.name);
    setIsRenaming(false);
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      void confirmRename();
    } else if (e.key === 'Escape') {
      cancelRename();
    }
  };

  return (
    <div className={styles.configCard}>
      <div className={styles.configCardHeader}>
        <div className={styles.configCardTitleArea}>
          {isRenaming ? (
            <input
              ref={inputRef}
              className={styles.renameInput}
              value={renameValue}
              maxLength={30}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={handleRenameKeyDown}
              onBlur={cancelRename}
            />
          ) : (
            <div
              className={styles.configCardName}
              aria-busy={pendingRenameValue !== null}
            >
              <span
                className={styles.configCardNameText}
                style={pendingRenameValue !== null ? { opacity: 0.6, fontStyle: 'italic' } : undefined}
                title={pendingRenameValue ?? profile.name}
              >
                {pendingRenameValue ?? profile.name}
              </span>
              {pendingRenameValue !== null ? (
                <RefreshCwIcon className="size-3 animate-spin" />
              ) : (
                // The Predefiniti placeholder can't be renamed — omit the pencil.
                !isPredefinita && (
                  <button
                    className={styles.renameButton}
                    onClick={startRename}
                    aria-label={t('savedProfiles.rename')}
                    title={t('savedProfiles.rename')}
                  >
                    ✎
                  </button>
                )
              )}
            </div>
          )}
          {isRebalancingProfile && <span className={styles.modeLabel}>{t('savedProfiles.rebalancingMode')}</span>}
        </div>
        <button
          className={styles.starButton}
          onClick={handleToggleDefault}
          // The placeholder is the fallback default — once active it can't be
          // deselected, so lock its star when it already holds the default.
          disabled={isTogglingDefault || (isPredefinita && profile.is_default)}
          aria-label={
            isPredefinita && profile.is_default
              ? 'Predefinita attiva'
              : profile.is_default
                ? 'Remove default'
                : 'Set as default'
          }
          aria-busy={isTogglingDefault}
        >
          {isTogglingDefault ? (
            <RefreshCwIcon className="size-4 animate-spin" />
          ) : (
            <StarIcon filled={profile.is_default} />
          )}
        </button>
      </div>

      <div className={styles.configCardBody}>
        {SECTION_LIST.map((cfg) => {
          const count = deltaCounts[cfg.type];
          if (!count) return null;
          return (
            <div key={cfg.type} className={styles.deltaRow}>
              <span className={styles.deltaLabel}>{cfg.label}</span>
              <span className={styles.deltaCount}>{count}</span>
            </div>
          );
        })}
        {Object.keys(deltaCounts).length === 0 && (
          <div className={styles.deltaRow}>
            <span className={styles.deltaLabel} style={{ color: '#999' }}>
              {/* The placeholder has no deltas (it *is* the baseline), so the
                  "no changes vs predefiniti" copy reads oddly there — show a
                  plain label instead. Saved configs keep the delta wording. */}
              {isPredefinita
                ? t('savedProfiles.predefinitaBody')
                : t('savedProfiles.noChanges')}
            </span>
          </div>
        )}
      </div>

      <div className={styles.configCardActions}>
        {/* The Predefiniti placeholder can't be deleted — keep only Apply. */}
        {!isPredefinita && (
          <button className={styles.textAction} onClick={() => onDelete(profile.id)}>
            {t('savedProfiles.delete')}
          </button>
        )}
        <FinecoButton click={() => onApply(profile)} copy={t('savedProfiles.apply')} />
      </div>
    </div>
  );
};
